﻿module Sm.UI.Form

open System
open Fable.DateFunctions
open MobF

open Sm.Core.Date

type IconName =
    | ArrowLeft
    | ArrowRight
    | BannerLogo
    | Box
    | Calendar
    | Cross
    | CrossSubtle
    | Loading
    | Logo
    | Offline
    | Pause
    | Play
    | Revert
    | Run
    | Sync
    | SyncDown
    | SyncError
    | SyncOk
    | SyncUp
    | Tick
    | Tools
    | Trophy
    | Warning
    | Zzz

module Control =
    type Content =
        | Empty
        | Label of string
        | Icon of IconName
        | IconAndLabel of IconName * string
    
    type ButtonState = {
        Disabled: bool
        Down: bool
        Subtle: bool
        Attributes: Map<string, string>
        Classes: string list
    }
    with
        static member Default = {
            Disabled = false
            Down = false
            Subtle = false
            Attributes = Map.empty
            Classes = List.empty
        }
    
    type Button<'T> = {
        Content: Content
        Command: 'T
        State: ButtonState
    }

module TextInput =
    type LayoutHint =
        | SuggestWidth of string
        | SuggestHeight of string
        | SuggestSize of width: string * height: string

    type FocusBehavior =
        | BrowserDefault
        | SelectAll
        | MoveToEnd

    type Config = {
        Hint: string
        InitialValue: string option
        Accept: string -> unit
        Reject: unit -> unit
        Validate: string option -> Result<unit, string>
        LayoutHint: LayoutHint option
        FocusBehavior: FocusBehavior option
        Multiline: bool
        Autoexpand: bool
        Classes: string list
    }
    with 
        static member Default = {
            Hint = ""
            InitialValue = None
            Accept = fun _ -> ()
            Reject = fun _ -> ()
            Validate = fun _ -> Ok ()
            LayoutHint = None
            FocusBehavior = None
            Multiline = false
            Autoexpand = false
            Classes = []
        }

    type State = {
        Config: Config
        EditValue: string option
        Submission: bool
    }

    type Msg =
        | Edit of string
        | Submit
        | Cancel

    type IComputed =
        abstract Value: string option
        abstract ValidationResult: Result<unit, string>
        abstract IsValid: bool

    type Type = Model<State, Msg, IComputed>

    let create config : Type =
        let init () =
            {
                Config = config
                EditValue = None
                Submission = false
            }

        let accept = config.Accept
        let reject = config.Reject

        let update (state, post, computed: IComputed) = function
            | Edit value ->
                { state with EditValue = Some value }

            | Submit when state.Submission = false ->
                post Submit
                { state with Submission = true }

            | Submit ->
                match computed.ValidationResult with
                | Error _ -> state
                | Ok _ ->
                    match computed.Value with
                    | None -> reject()
                    | Some value -> accept value
                    { state with EditValue = None; Submission = false }

            | Cancel ->
                reject()
                { state with EditValue = None; Submission = false }

        let compute state =
            { new IComputed with
                member _.Value =
                    if state.EditValue.IsNone then state.Config.InitialValue
                    else state.EditValue

                member this.ValidationResult =
                    if state.Submission then state.Config.Validate this.Value
                    else Ok ()

                member this.IsValid =
                    match this.ValidationResult with
                    | Ok _ -> true
                    | Error _ -> false
            }

        Model.useInit init
        |> Model.andUpdateWithComputed update
        |> Model.createWithComputed compute

module DateInput =
    open Control

    type Availability =
        | Unavailable
        | Busy
        | Available

    type Day<'T> = {
        Date: DateOnly
        Availability: Availability
        IsToday: bool
        IsCurrentMonth: bool
        IsSelected: bool
        IsMarked: bool
    }

    type Config<'T> = {
        InitialValue: DateOnly option
        AcceptMessage: DateOnly -> 'T
        MapAvailability: DateOnly -> Availability
        PermitMonth: DateOnly -> bool
    }

    type State<'T> = {
        Config: Config<'T>
        ActiveMonth: DateOnly
    }

    type Msg =
        | PreviousMonth
        | NextMonth
        | PreviousYear
        | NextYear
        | CurrentMonth

    type IComputed<'T> =
        abstract Days: Day<'T> list
        abstract PreviousMonth: Button<Msg>
        abstract NextMonth: Button<Msg>
        abstract PreviousYear: Button<Msg>
        abstract NextYear: Button<Msg>
        abstract Current: Button<Msg>
        abstract MonthLabel: string
        abstract YearLabel: string

    type Type<'T> = Model<State<'T>, Msg, IComputed<'T>>

    let inline create config : Type<'T> =
        let inline firstOfMonth (d: DateOnly) = DateOnly(d.Year, d.Month, 1)

        let today = DateOnly.Today
        let currentMonth = firstOfMonth today

        let initialMonth =
            match config.InitialValue with
            | Some value -> firstOfMonth value
            | None -> currentMonth

        let permit actual =
            let test = firstOfMonth actual
            config.PermitMonth test

        let tryMapDate f current =
            let proposed = f current
            if permit proposed then proposed
            else current

        let inline addMonths x (d: DateOnly) = d.AddMonths(x)
        let inline addYears x (d: DateOnly) = d.AddYears(x)

        let init () =
            {
                Config = config
                ActiveMonth = initialMonth
            }

        let update state = function
            | PreviousMonth ->
                { state with
                    ActiveMonth = state.ActiveMonth |> tryMapDate (addMonths -1)
                }

            | NextMonth ->
                { state with
                    ActiveMonth = state.ActiveMonth |> tryMapDate (addMonths 1)
                }

            | PreviousYear ->
                { state with
                    ActiveMonth = state.ActiveMonth |> tryMapDate (addYears -1)
                }

            | NextYear ->
                { state with
                    ActiveMonth = state.ActiveMonth |> tryMapDate (addYears 1)
                }

            | CurrentMonth ->
                { state with ActiveMonth = currentMonth } 

        let compute state =
            let deny f =
                state.ActiveMonth
                |> f
                |> (permit >> not)

            { new IComputed<_> with
                member _.Days =
                    [
                        let start = (startOfWeek << firstOfMonth) state.ActiveMonth
                        let weeks = getWeeksInMonth state.ActiveMonth.DateTime
                        let count = weeks * 7

                        for x in 0 .. count - 1 do
                            let date = start.AddDays(x)

                            {
                                Date = date
                                Availability = config.MapAvailability date
                                IsToday = date = today
                                IsCurrentMonth = sameMonth today date
                                IsMarked = false
                                IsSelected =
                                    config.InitialValue 
                                    |> Option.map ((=) date)
                                    |> Option.defaultValue false
                            }
                    ]

                member _.PreviousMonth =
                    {
                        Content = Icon ArrowLeft
                        Command = PreviousMonth
                        State = 
                            { ButtonState.Default with
                                Classes = ["tool"]
                                Disabled = deny (addMonths -1)
                            }
                    }

                member _.NextMonth =
                    {
                        Content = Icon ArrowRight
                        Command = NextMonth
                        State = 
                            { ButtonState.Default with
                                Classes = ["tool"]
                                Disabled = deny (addMonths 1)
                            }
                    }

                member _.PreviousYear =
                    {
                        Content = Icon ArrowLeft
                        Command = PreviousYear
                        State =
                            { ButtonState.Default with
                                Classes = ["tool"]
                                Disabled = deny (addYears -1)
                            }
                    }

                member _.NextYear =
                    {
                        Content = Icon ArrowRight
                        Command = NextYear
                        State =
                            { ButtonState.Default with
                                Classes = ["tool"]
                                Disabled = deny (addYears 1)
                            }
                    }

                member _.Current =
                    {
                        Content = Label "Current"
                        Command = CurrentMonth
                        State =
                            { ButtonState.Default with
                                Classes = ["tool"]
                                Disabled = sameMonth state.ActiveMonth currentMonth
                            }
                    }

                member _.MonthLabel =
                    state.ActiveMonth.DateTime.Format("MMM")

                member _.YearLabel =
                    state.ActiveMonth.DateTime.Format("yyyy")
            }

        Model.useInit init
        |> Model.andUpdate update
        |> Model.createWithComputed compute

module Basic =
    open Control

    type Status =
        | Ready
        | Pending
        | Invalid of string list

    type Field = {
        Label: string
        Name: string
        Type: string
        Value: string
    }

    module Field =
        let text label name =
            { Label = label; Name = name; Type = "text"; Value = "" }

        let email label name =
            { Label = label; Name = name; Type = "email"; Value = "" }

        let password label name =
            { Label = label; Name = name; Type = "password"; Value = "" }

    type State = {
        Fields: Field list
        Status: Status
    }

    type Msg =
        | EditField of string * string
        | Notify of string list
        | Submit
        | Complete

    type Computed =
        abstract Submitting: bool
        abstract Messages: string list
        abstract Submit: Button<Msg>

    type Options = {
        SubmitLabel: string
        FieldDefinitions: Field list
        HandleSubmission: Map<string, string> -> Fable.Core.JS.Promise<Result<unit, string list>>
    }

    type BasicForm = Model<State, Msg, Computed>

    let create options: BasicForm =
        let init () =
            {
                Fields = options.FieldDefinitions
                Status = Ready
            }

        let update (state, post) = function
            | EditField (name, value) ->
                let fields =
                    state.Fields
                    |> List.map (function
                        | { Name = actual } as f when actual = name -> { f with Value = value }
                        | f -> f
                    )
                { state with Fields = fields }

            | Submit ->
                promise {
                    let data =
                        state.Fields
                        |> Seq.map (fun f -> f.Name, f.Value)
                        |> Map

                    match! options.HandleSubmission data with
                    | Ok _ -> post Complete
                    | Error messages -> post (Notify messages)
                }
                |> Promise.start

                { state with Status = Pending }

            | Notify messages ->
                let status = Invalid messages
                { state with Status = status }

            | Complete ->
                { state with Status = Ready }

        let compute state =
            { new Computed with
                member _.Submitting =
                    match state.Status with 
                    | Pending -> true
                    | _ -> false

                member _.Messages =
                    match state.Status with
                    | Invalid messages -> messages
                    | _ -> []

                member this.Submit =
                    {
                        Content =
                            if this.Submitting then Icon Loading
                            else Label options.SubmitLabel
                        Command = Submit
                        State =
                            { ButtonState.Default with
                                Classes = if this.Submitting then ["is-submitting"] else []
                                Disabled = this.Submitting
                            }
                    }
            }

        Model.useInit init
        |> Model.andUpdateWithPost update
        |> Model.createWithComputed compute
